import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  ShoppingCartCheckout as ReorderIcon,
} from '@mui/icons-material';
import { Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrders } from '../hooks/useOrders';
import { useInvoice } from '../hooks/useInvoice';
import { useApiCart } from '../hooks/useApiCartProvider';
import { Order } from '../services';
import { computeBreakdown, formatIDR } from '../utils/pricing';
import ReorderDialog from '../components/orders/ReorderDialog';

const getStatusColor = (status: Order['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'DRAFT': return 'warning';
    case 'SUBMITTED': return 'primary';
    case 'CONFIRMED': return 'info';
    case 'SHIPPED': return 'secondary';
    case 'DELIVERED': return 'success';
    case 'INVOICED': return 'success';
    case 'CLOSED': return 'default';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const {
    currentOrder,
    error,
    loadOrderById,
    clearError,
  } = useOrders();

  const {
    isLoading: invoiceLoading,
    isGenerating,
    infoMessage: invoiceInfoMessage,
    getInvoice,
    generateInvoice,
    downloadInvoice,
    clearInfoMessage,
  } = useInvoice();

  const { t } = useTranslation();
  const { totalItems: cartItemCount, loadCart } = useApiCart();
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderToast, setReorderToast] = useState('');

  useEffect(() => {
    if (id) {
      setLoading(true);
      loadOrderById(id).finally(() => setLoading(false));
    }
  }, [id, loadOrderById]);

  const handleBack = () => {
    navigate('/orders');
  };

  const handleGenerateInvoice = async (orderId: string) => {
    try {
      const result = await generateInvoice(orderId);
      if (result.success && result.download_url) {
        // Optionally auto-download after generation
        // window.open(result.download_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate invoice:', error);
    }
  };

  const handleDownloadInvoice = async (orderId: string) => {
    try {
      const invoiceResult = await getInvoice(orderId);
      if (invoiceResult.success && invoiceResult.invoice_id) {
        await downloadInvoice(invoiceResult.invoice_id);
      } else if (invoiceResult.success === false && invoiceResult.message?.includes('not generated')) {
        // Invoice not generated yet, offer to generate
        await handleGenerateInvoice(orderId);
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const isInvoiceAvailable = (order: Order) => {
    return order.status === 'INVOICED' || order.status === 'CLOSED';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !currentOrder) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          {t('orders.backToOrders')}
        </Button>
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('orders.notFound')}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          {t('orders.backToOrders')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          component="button"
          variant="body1"
          onClick={handleBack}
          sx={{ textDecoration: 'underline' }}
        >
          {t('orders.title')}
        </Link>
        <Typography color="text.primary">
          Order #{currentOrder.order_number}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1">
            Order #{currentOrder.order_number}
          </Typography>
          {currentOrder.created_via === 'epicor' && (
            <Chip
              label={t('orders.offlineBadge')}
              color="default"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Reorder hidden for offline orders — backend also refuses
              with 403. Customer needs to contact sales rep. */}
          {currentOrder.created_via !== 'epicor' && (
            <Button
              variant="contained"
              startIcon={<ReorderIcon />}
              onClick={() => setReorderOpen(true)}
            >
              {t('orders.reorder')}
            </Button>
          )}
          {isInvoiceAvailable(currentOrder) && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<ReceiptIcon />}
              onClick={() => handleDownloadInvoice(currentOrder.id)}
              disabled={invoiceLoading || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Download Invoice'}
            </Button>
          )}
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            {t('orders.backToOrders')}
          </Button>
        </Box>
      </Box>

      {currentOrder.created_via === 'epicor' && (
        <Alert severity="info" sx={{ mb: 3 }} icon={false}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
            {t('orders.offlineDetail.title')}
          </Typography>
          <Typography variant="body2" sx={{ mb: currentOrder.sales_rep_name ? 1.5 : 0 }}>
            {t('orders.offlineDetail.noteHidden')}
          </Typography>
          {currentOrder.sales_rep_name && (
            <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                {t('orders.offlineDetail.salesRep')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {currentOrder.sales_rep_name}
              </Typography>
              {currentOrder.sales_rep_code && (
                <Typography variant="caption" color="text.secondary">
                  {t('orders.offlineDetail.salesRepCode', 'Code')}: {currentOrder.sales_rep_code}
                </Typography>
              )}
            </Box>
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {invoiceInfoMessage && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={clearInfoMessage}>
          {invoiceInfoMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('orders.orderInformation')}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.status')}
              </Typography>
              <Chip
                label={currentOrder.status.charAt(0).toUpperCase() + currentOrder.status.slice(1)}
                color={getStatusColor(currentOrder.status)}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.orderDate')}
              </Typography>
              <Typography variant="body1">
                {formatDate(currentOrder.created_at)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.totalAmount')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {formatIDR(
                  currentOrder.grand_total_amount ??
                    computeBreakdown(
                      currentOrder.original_total_amount ?? currentOrder.total_amount,
                      currentOrder.total_discount_amount ?? 0,
                    ).total,
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.itemsCount')}
              </Typography>
              <Typography variant="body1">
                {currentOrder.item_count} items
              </Typography>
            </Grid>
            {currentOrder.shipping_date && (
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('orders.shippingDate')}
                </Typography>
                <Typography variant="body1">
                  {formatDate(currentOrder.shipping_date)}
                </Typography>
              </Grid>
            )}
            {currentOrder.invoice_date && (
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('orders.invoiceDate')}
                </Typography>
                <Typography variant="body1">
                  {formatDate(currentOrder.invoice_date)}
                </Typography>
              </Grid>
            )}
            {currentOrder.epicor_order_num && (
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('orders.epicorOrderNumber')}
                </Typography>
                <Typography variant="body1">
                  #{currentOrder.epicor_order_num}
                </Typography>
              </Grid>
            )}
            {currentOrder.epicor_order_status && (
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('orders.epicorStatus')}
                </Typography>
                <Typography variant="body1">
                  {currentOrder.epicor_order_status}
                </Typography>
              </Grid>
            )}
            {currentOrder.notes && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('orders.notes')}
                </Typography>
                <Typography variant="body1">
                  {currentOrder.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('orders.orderItems')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('cart.item')}</TableCell>
                  <TableCell>{t('products.description')}</TableCell>
                  <TableCell align="right">{t('cart.quantity')}</TableCell>
                  <TableCell align="right">{t('orders.unitPrice')}</TableCell>
                  <TableCell align="right">{t('cart.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentOrder.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {item.product_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.epicor_part_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatIDR(item.unit_price)}</TableCell>
                    <TableCell align="right">{formatIDR(item.total_price)}</TableCell>
                  </TableRow>
                ))}
                {(() => {
                  // 4-line breakdown. Price list is VAT-inclusive so the
                  // PPN line is intentionally omitted — total = subtotal.
                  // DMS invoice still surfaces PPN broken out.
                  const original =
                    currentOrder.original_total_amount ?? currentOrder.total_amount;
                  const discount = currentOrder.total_discount_amount ?? 0;
                  const fb = computeBreakdown(original, discount);
                  const sellingPrice = original;
                  const subtotal = currentOrder.subtotal_amount ?? fb.subtotal;
                  const total = subtotal;
                  const rows: Array<{
                    label: string;
                    value: string;
                    bold?: boolean;
                    color?: string;
                  }> = [
                    { label: 'Total Selling Price', value: formatIDR(sellingPrice) },
                    {
                      label: 'Discount (Rp)',
                      value:
                        discount > 0
                          ? `- ${formatIDR(discount)}`
                          : formatIDR(0),
                      color: discount > 0 ? 'success.main' : undefined,
                    },
                    { label: 'Subtotal', value: formatIDR(subtotal) },
                    { label: 'Total', value: formatIDR(total), bold: true },
                  ];
                  return rows.map((r, i) => (
                    <TableRow key={`summary-${i}`}>
                      <TableCell
                        colSpan={4}
                        align="right"
                        sx={{
                          fontWeight: r.bold ? 'bold' : 'normal',
                          borderBottom: i === rows.length - 1 ? undefined : 0,
                          py: 0.75,
                          color: r.color,
                        }}
                      >
                        {r.label}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: r.bold ? 'bold' : 'normal',
                          borderBottom: i === rows.length - 1 ? undefined : 0,
                          py: 0.75,
                          color: r.color,
                        }}
                      >
                        {r.value}
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Reorder confirmation dialog */}
      <ReorderDialog
        open={reorderOpen}
        order={currentOrder}
        cartItemCount={cartItemCount}
        onClose={() => setReorderOpen(false)}
        onSuccess={async (msg) => {
          setReorderOpen(false);
          await loadCart();
          setReorderToast(msg);
          setTimeout(() => navigate('/cart'), 600);
        }}
      />
      <Snackbar
        open={!!reorderToast}
        autoHideDuration={4000}
        onClose={() => setReorderToast('')}
        message={reorderToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default OrderDetailPage;